import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {keepPreviousData, useQueryClient} from '@tanstack/react-query';
import {PagesSidePanel} from './components/pages-side-panel';
import {StudioChatPanel} from './components/studio-chat-panel';
import {StudioLayout, type StudioTool} from './components/studio-layout';
import {LandingPagePanel} from './components/landing-page-panel';
import {LandingPageCanvasPanel} from './components/canvas/landing-page-canvas-panel';
import {LandingPageErrorState} from './components/landing-page-error-state';
import {PreviewSidebar} from './components/preview-sidebar';
import {GeneratingOverlay} from './components/generating-overlay';
import {CancelGenerationModal} from './components/cancel-generation-modal';
import {EmbeddedBlockVariant, EmbeddedPage, ReadCampaign} from "@tinct/core-api-models";
import {invalidatePageQueries, useCancelGeneration, useRegeneratePage, useExcludePage, useIncludePage, useGetPageById, usePatchBlockVariant} from '@tinct/core-api';
import {useGetCurrentQuotas, useGetCurrentWorkspaceId} from '@tinct/core-workspace';
import {useStudioHostUrls} from './studio-host-context';
import {useConfig} from '@tinct/core-config';
import {useOpenPaywall} from '@tinct/web-feature-payment';
import {toast} from 'sonner';
import {pluralize} from '@tinct/core-utils';
import {ResumeBuyerImportModal} from '@tinct/web-feature-companies';
import {BlockDetailsSidebar} from '../landing-page-editor/components/block-details-sidebar';
import {ChangesNotLiveModal} from '../../modals/changes-not-live-modal/changes-not-live-modal';
import {useStudioTab} from './components/studio-mode-tabs';
import {isTargetPage, sortTargetPages, useBackgroundGenerationJobAlert, useCampaignEditor, useLogoRematch, usePublishedEditNotice, useStudioViewMode} from './hooks';
import {cacheBustHtmlKeys} from '../../utils/cache-bust-html-keys';
import {isBlockPersonalizationEnabled} from '../../utils/is-block-personalization-enabled';

// Selection for each company logo image variant: url to render, natural
// dimensions to fit customer-logo template slots to the logo's aspect ratio.
const logoImageFields = {
  fields: {
    id: null,
    url: null,
    originalWidth: null,
    originalHeight: null,
  },
};

// Only the selected page's variants are ever rendered, so they are fetched per
// page instead of being embedded in the (potentially huge) campaign payload.
const pageVariantFields = {
  id: null,
  blockVariants: {
    limit: 200,
    fields: {
      id: null,
      cssSelectors: null,
      value: null,
      valueAsText: null,
      status: null,
      type: null,
      sectionCssSelector: null,
      label: null,
      companies: {
        fields: {
          id: null,
          name: null,
          logo: logoImageFields,
          brandmarkLogo: logoImageFields,
          brandmarkLogoGreyscale: logoImageFields,
          logoGreyscale: logoImageFields,
        }
      },
      createdAt: null,
      updatedAt: null,
      block: {
        fields: {
          id: null,
        }
      }
    }
  }
};

const isGenerationPending = (status?: string | null) =>
  status === 'queued' || status === 'running';

export type EditorStepState = {
  isSaving: boolean;
  /** An enabled customer_logo block exists but the buyer pool is empty. */
  isLogoPoolEmpty: boolean;
  /**
   * Number of generated variant pages — the hosts render the top-bar
   * "Original page / Page variants" tabs and need the badge count.
   */
  variantsCount: number;
};

export type LandingPageStudioProps = {
  campaign: ReadCampaign;
  onGenerateAll?: () => void;
  /** Editor-mode state the funnel needs to gate its Continue button. */
  onEditorStateChange?: (state: EditorStepState) => void;
};

/**
 * The merged editor + previewer screen. The floating pages panel drives the
 * mode: with the original page selected (`selectedPageId === null`) the canvas
 * is the personalization-scope editor; with a generated variant selected it is
 * the per-account previewer.
 */
export const LandingPageStudio = ({
  campaign,
  onGenerateAll,
  onEditorStateChange,
}: LandingPageStudioProps) => {
  const campaignId = campaign.id as string;

  // Both origins consume the version's packaged-artifact URLs (htmlKeys),
  // cache-busted with the version's packagedAt (stamped when the artifact was
  // written): artifacts are re-packaged in place — custom-code saves,
  // re-analysis — and the new token is what remounts the preview on the new
  // document (preview-iframe contract, rule 1).
  const isNewOrigin = campaign.landingPageOriginType === 'new';
  const currentVersion = campaign.landingPage?.currentVersion ?? null;
  const landingPageUrls = useMemo(
    () => cacheBustHtmlKeys(currentVersion?.htmlKeys, currentVersion?.packagedAt),
    [currentVersion?.htmlKeys, currentVersion?.packagedAt],
  );
  const pages = useMemo(() => campaign.pages ?? [], [campaign.pages]);

  const { isFeatureEnabled } = useConfig();
  const logoFeatureEnabled = isFeatureEnabled('logosPersonalization');

  // Hide customer_logo blocks when the feature is off, same as the editor
  const previewBlocks = useMemo(() => {
    const campaignBlocks = campaign.blocks ?? [];
    return logoFeatureEnabled
      ? campaignBlocks
      : campaignBlocks.filter((block) => block.type !== 'customer_logo');
  }, [campaign.blocks, logoFeatureEnabled]);

  const [searchParams] = useSearchParams();
  const initialPageIdParam = searchParams.get('pageId');
  const initialSearchQuery = searchParams.get('q') ?? '';

  // Editor vs previewer is now driven by the top-bar "Original page / Page
  // variants" tabs through the URL (`?view=`); a `?pageId=` deep link opens
  // the variants side (see useStudioTab).
  const [activeTab] = useStudioTab();
  const isOriginalSelected = activeTab === 'original';

  // Panel display order (ready first) — [0] is the page shown at the top of
  // the variants panel, which the tab default-selects below.
  const generatedPages = useMemo(() => sortTargetPages(pages.filter(isTargetPage)), [pages]);

  const [selectedPageId, setSelectedPageId] = useState<string | null>(
    () => {
      if (initialPageIdParam && pages.some((p) => p.id === initialPageIdParam)) {
        return initialPageIdParam;
      }
      return null;
    }
  );

  // The variants tab always shows a page: with no explicit selection — or a
  // stale one that no longer resolves — it falls back to the first generated
  // variant (the top of the panel list).
  const effectiveSelectedPageId = isOriginalSelected
    ? null
    : selectedPageId && pages.some((p) => p.id === selectedPageId)
      ? selectedPageId
      : generatedPages[0]?.id ?? null;

  const selectedPage = useMemo(
    () => pages.find((p) => p.id === effectiveSelectedPageId) ?? null,
    [pages, effectiveSelectedPageId],
  );

  // ==========================================================================
  // Editor mode — the original page's personalization scope
  // ==========================================================================

  const editor = useCampaignEditor({ campaignId });

  useEffect(() => {
    onEditorStateChange?.({
      isSaving: editor.isSaving,
      isLogoPoolEmpty: editor.isLogoPoolEmpty,
      variantsCount: generatedPages.length,
    });
  }, [editor.isSaving, editor.isLogoPoolEmpty, generatedPages.length, onEditorStateChange]);

  const isLibraryNotice = editor.changesNotLiveReason === 'customer-library';

  // Customers-library changes re-match this campaign's logos in place (a job,
  // no regeneration); the notice is only raised when a job actually started.
  const logoRematch = useLogoRematch({
    campaignId,
    onJobStarted: editor.notifyCustomerLibraryChanged,
  });

  // ==========================================================================
  // Previewer mode — the selected variant page
  // ==========================================================================

  // The selected page's block variants, fetched on demand. `keepPreviousData`
  // holds the previous page's variants while the next ones load, so switching
  // accounts never flashes the unpersonalized landing page.
  const { data: selectedPageDetail } = useGetPageById(
    { id: effectiveSelectedPageId ?? '', fields: pageVariantFields },
    { enabled: !!effectiveSelectedPageId, placeholderData: keepPreviousData },
  );

  // Re-attach the full campaign block to each variant: the page endpoint only
  // returns a block ref, but everything downstream (panel, overlays, sidebar)
  // is typed against the embedded shape. Built from the unfiltered blocks so no
  // variant loses its block when the logos feature is off.
  const blocksById = useMemo(
    () => new Map((campaign.blocks ?? []).map((block) => [block.id, block])),
    [campaign.blocks]
  );

  const blockVariants = useMemo<EmbeddedBlockVariant[]>(
    () =>
      (selectedPageDetail?.blockVariants ?? []).map((variant) => ({
        ...variant,
        block: (variant.block?.id ? blocksById.get(variant.block.id) : null) ?? null,
      })),
    [selectedPageDetail?.blockVariants, blocksById]
  );

  // `keepPreviousData` means `blockVariants` still holds the *previous* page's
  // variants while the newly selected one loads. The panel wants that (it's what
  // avoids the unpersonalized flash), but anything pairing a variant with a page
  // id — the sidebar and its mutations — must wait for the matching payload.
  const areVariantsForSelectedPage =
    !!effectiveSelectedPageId && selectedPageDetail?.id === effectiveSelectedPageId;

  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [hideBlocks, setHideBlocks] = useState(false);
  // Hover-driven (the panel expands under the pointer). Lives here (not in
  // the panel) so it survives the page component remounts that come with
  // view-mode switches.
  const [isPagesPanelCollapsed, setIsPagesPanelCollapsed] = useState(true);

  // Bottom-toolbar interaction tool. `text` = inline text edition on the
  // previewer; only meaningful on the variants tab.
  const [activeTool, setActiveTool] = useState<StudioTool>('select');

  const handleToolChange = useCallback((tool: StudioTool) => {
    setActiveTool(tool);
    if (tool === 'text') {
      // Inline editing replaces the sidebar workflow, and hidden blocks would
      // leave nothing to click — reset both.
      setSelectedBlockId(null);
      setHideBlocks(false);
    }
  }, []);

  const handleHideBlocksChange = useCallback((hidden: boolean) => {
    setHideBlocks(hidden);
    // A hidden block can't stay selected — its sidebar would edit nothing visible.
    if (hidden) setSelectedBlockId(null);
  }, []);

  // Editor and previewer select different things — drop the block selection
  // (and the text tool) when the top-bar tab flips (render-time adjustment,
  // not an effect, so the stale selection never paints).
  const [prevTab, setPrevTab] = useState(activeTab);
  if (prevTab !== activeTab) {
    setPrevTab(activeTab);
    setSelectedBlockId(null);
    setActiveTool('select');
  }

  // Single viewport vs the multi-breakpoint canvas (persisted). Both panels
  // share the same prop shape, so the mode just swaps the component.
  const [viewMode, setViewMode] = useStudioViewMode();
  const PanelComponent = viewMode === 'canvas' ? LandingPageCanvasPanel : LandingPagePanel;

  const {lpGenerationQuota} = useGetCurrentQuotas();
  const {openPaywall} = useOpenPaywall();
  const workspaceId = useGetCurrentWorkspaceId();
  const hostUrls = useStudioHostUrls(campaignId);
  const navigate = useNavigate();

  const activeBlocks = isOriginalSelected ? editor.blocks : previewBlocks;
  const selectedBlock = activeBlocks?.find((b) => b.id === selectedBlockId) ?? null;

  const selectedVariant = !isOriginalSelected && selectedBlockId
    ? (blockVariants.find((v) => v.block?.id === selectedBlockId) ?? null)
    : null;

  const handleSelectPage = useCallback(
    (page: EmbeddedPage) => {
      setSelectedPageId((current) => {
        if (page.id !== current) setSelectedBlockId(null);
        return page.id ?? current;
      });
    },
    []
  );

  const handleBlockSelect = useCallback((blockId: string | null) => {
    setSelectedBlockId(blockId);
  }, []);

  const handleDeselectBlock = useCallback(() => {
    setSelectedBlockId(null);
  }, []);

  // ==========================================================================
  // Inline text edition (text tool) — same save path as the preview sidebar:
  // PATCH the block variant's `value`, then surface the republish notice when
  // a published, in-sync page just went stale.
  // ==========================================================================

  const { mutateAsync: patchBlockVariant } = usePatchBlockVariant();
  const isSelectedPageInSync =
    !selectedPage?.outdatedStatus || selectedPage.outdatedStatus === 'none';
  const { notify: notifyInlineEdit, modalProps: inlineEditModalProps } =
    usePublishedEditNotice(selectedPage?.status === 'published' && isSelectedPageInSync);

  const handleVariantEditCommit = useCallback(
    async (blockVariantId: string, value: string) => {
      // keepPreviousData guard: only save against the page the rendered
      // variants actually belong to.
      const pageId = selectedPageDetail?.id;
      if (!pageId || !areVariantsForSelectedPage) {
        throw new Error('No variant page selected');
      }
      let updatedVariant;
      try {
        updatedVariant = await patchBlockVariant({ pageId, blockVariantId, data: { value } });
      } catch (error) {
        toast.error('Could not save the text change. Please try again.');
        throw error;
      }
      notifyInlineEdit();
      // The backend validates/processes the submitted text — hand the
      // processed value back so the block shows the server's result.
      return updatedVariant.value;
    },
    [selectedPageDetail?.id, areVariantsForSelectedPage, patchBlockVariant, notifyInlineEdit],
  );

  const regeneratePage = useRegeneratePage({
    onSuccess: () => toast.success('Page regeneration started'),
    onError: () => toast.error('Failed to regenerate page'),
  });

  const excludePage = useExcludePage({
    onSuccess: () => toast.success('Page excluded from publishing'),
    onError: () => toast.error('Failed to exclude page'),
  });

  const includePage = useIncludePage({
    onSuccess: () => toast.success('Page included in publishing'),
    onError: () => toast.error('Failed to include page'),
  });

  const handleRetry = () => {
    if (selectedPage?.id) {
      regeneratePage.mutate({ pageId: selectedPage.id, organizationId: workspaceId });
    }
  };

  const handleToggleExclude = useCallback((page: EmbeddedPage) => {
    if (!page.id) return;
    if (page.status === 'excluded') {
      includePage.mutate({ pageId: page.id });
    } else {
      excludePage.mutate({ pageId: page.id });
    }
  }, [excludePage, includePage]);

  // Generating overlay CTAs (F-120): leave with the job tracked in the
  // background widget, or cancel what is still queued (after confirming).
  const { runInBackground } = useBackgroundGenerationJobAlert(campaignId, selectedPage);
  const [showCancelGeneration, setShowCancelGeneration] = useState(false);
  const cancelGeneration = useCancelGeneration({
    onSuccess: (result) =>
      toast.success(
        `Cancelled ${result.jobsCancelled} queued ${pluralize(result.jobsCancelled, 'page')}`,
      ),
    onError: () => toast.error('Failed to cancel generation'),
  });
  const handleCancelGenerationConfirmed = () => {
    cancelGeneration.mutate(
      { id: campaignId },
      { onSettled: () => setShowCancelGeneration(false) },
    );
  };

  // Open paywall when a selected page failed due to insufficient credits (and quota is still 0)
  useEffect(() => {
    if (
      selectedPage?.status === 'error' &&
      selectedPage?.errorCode === 'not_enough_credits' &&
      (lpGenerationQuota?.remainingQuota ?? 0) <= 0
    ) {
      openPaywall('not_enough_credits');
    }
  }, [selectedPage?.id, selectedPage?.status, selectedPage?.errorCode, lpGenerationQuota?.remainingQuota, openPaywall]);

  const isGenerating =
    (campaign.statistics?.generationJobsStatistics?.runningCount ?? 0) +
    (campaign.statistics?.generationJobsStatistics?.queuedCount ?? 0) > 0;

  // The campaign poll no longer carries variant content, so the per-page query
  // has to be refreshed itself once the page's generation job settles. Tracked
  // for *every* page, not just the selected one: a page can finish while another
  // is being viewed, and with the app-wide `staleTime: Infinity` its cached
  // pre-generation variants would then be served forever on the way back.
  const queryClient = useQueryClient();
  const previousGenerationStatuses = useRef(new Map<string, string | null>());

  useEffect(() => {
    const previous = previousGenerationStatuses.current;
    const next = new Map<string, string | null>();

    for (const page of pages) {
      if (!page.id) continue;

      const status = page.lastGenerationJob?.status ?? null;
      next.set(page.id, status);

      // Unknown page id (first run, newly added page) means no transition to
      // observe — only a pending → settled move invalidates.
      if (!previous.has(page.id)) continue;

      if (isGenerationPending(previous.get(page.id)) && !isGenerationPending(status)) {
        // Same scope as the Ask Tinct rewrites: the page detail, never the
        // chat-message queries sharing its key prefix.
        void invalidatePageQueries(queryClient, page.id);
      }
    }

    previousGenerationStatuses.current = next;
  }, [pages, queryClient]);

  // Close the editor's block-details sidebar when clicking outside of it
  const sidebarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOriginalSelected || !selectedBlockId) return;

    const handlePointerDown = (e: PointerEvent) => {
      // Modals (e.g. the import-buyer modal) and other Radix overlays render in
      // a portal outside the sidebar. While one is open the user is
      // interacting with it, so don't treat those clicks as "outside" clicks
      // that would deselect the block and unmount the sidebar. This must run
      // capture-phase on pointerdown: Radix dismisses the dialog on the same
      // pointerdown, so any later handler would see it as already closed and
      // close the sidebar along with the modal.
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;

      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target as Node)
      ) {
        setSelectedBlockId(null);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [isOriginalSelected, selectedBlockId]);

  return (
    <>
      <StudioLayout
        chatPanel={
          // EXISTING-origin original page: the personalization-scope editor
          // has no chat agent (the page-variant chat is per variant, and the
          // edit chat only exists for Tinct-hosted NEW-origin pages) — give
          // the canvas the full width instead of a placeholder column.
          isOriginalSelected && !isNewOrigin ? undefined : (
            <StudioChatPanel
              campaignId={campaignId}
              page={selectedPage}
              block={isOriginalSelected ? null : selectedBlock}
              onDeselectBlock={handleDeselectBlock}
              // An ask on a variant while it regenerates would target content
              // about to be overwritten — hold the chat until its job lands.
              // Other (settled) variants keep their chat usable.
              disabled={
                !isOriginalSelected &&
                isGenerationPending(selectedPage?.lastGenerationJob?.status)
              }
            />
          )
        }
        // Variants only: the original page has no contextualised content to
        // peek behind, so the toolbar drops the checkbox and its shortcut.
        hideBlocks={isOriginalSelected ? undefined : hideBlocks}
        onHideBlocksChange={isOriginalSelected ? undefined : handleHideBlocksChange}
        // Inline text edition needs block variants — variants tab only.
        tool={!isOriginalSelected ? activeTool : undefined}
        onToolChange={handleToolChange}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      >
        {isOriginalSelected ? (
            <PanelComponent
              key={`${campaignId}-editor-${viewMode}`}
              landingPageUrls={landingPageUrls}
              loading={isNewOrigin && !landingPageUrls}
              // Not gated on `hideBlocks`: the toggle is variants-only, and a
              // value left over from the variants tab must not hide the
              // editor's blocks with nothing on screen to reset it.
              blocks={editor.blocks}
              includeDisabledBlocks
              onBlockSelect={handleBlockSelect}
              onBlockUpdate={editor.handleBlockUpdate}
              selectedBlockId={selectedBlockId}
            />
          ) : (
            <PanelComponent
              key={`${campaignId}-${viewMode}`}
              landingPageUrls={landingPageUrls}
              loading={isNewOrigin && !landingPageUrls}
              blocks={hideBlocks ? [] : previewBlocks}
              onBlockSelect={handleBlockSelect}
              selectedBlockId={selectedBlockId}
              blockVariants={blockVariants}
              interactionMode={activeTool}
              onVariantEditCommit={handleVariantEditCommit}
              // Pages panel inside the canvas card's left edge — variants
              // mode only; the top-bar tabs are the way back to the original
              // page.
              sidePanel={
                <PagesSidePanel
                  pages={pages}
                  campaign={campaign}
                  selectedPageId={effectiveSelectedPageId}
                  collapsed={isPagesPanelCollapsed}
                  onCollapsedChange={setIsPagesPanelCollapsed}
                  onSelectPage={handleSelectPage}
                  onGenerateAll={onGenerateAll}
                  isGenerating={isGenerating}
                  onToggleExclude={handleToggleExclude}
                  initialSearchQuery={initialSearchQuery}
                  isAdwordAxis={campaign.landingPageVariationAxis === 'adword'}
                />
              }
              errorState={selectedPage?.status === 'error' ? (
                <LandingPageErrorState
                  onRetry={handleRetry}
                  isRetrying={regeneratePage.isPending}
                  errorCode={selectedPage.errorCode}
                />
              ) : undefined}
              overlay={isGenerationPending(selectedPage?.lastGenerationJob?.status) ? (
                <GeneratingOverlay
                  remainingCount={
                    (campaign.statistics?.generationJobsStatistics?.runningCount ?? 0) +
                    (campaign.statistics?.generationJobsStatistics?.queuedCount ?? 0)}
                  completedCount={campaign.statistics?.generationJobsStatistics?.completedCount ?? 0}
                  totalCount={
                    (campaign.statistics?.generationJobsStatistics?.completedCount ?? 0) +
                    (campaign.statistics?.generationJobsStatistics?.runningCount ?? 0) +
                    (campaign.statistics?.generationJobsStatistics?.queuedCount ?? 0)}
                  estimatedTimeLeftInSeconds={campaign.statistics?.generationJobsStatistics?.estimatedJobsProcessingTimeLeftInSeconds}
                  onLetRunInBackground={runInBackground}
                  onCancel={() => setShowCancelGeneration(true)}
                />
              ) : undefined}
            />
          )}

        {/* Right sidebar — logo settings (editor) / variant details (previewer).
            Other blocks are edited inline in their badge; a disabled logo
            block has no settings to show. */}
        {isOriginalSelected &&
          selectedBlock?.type === 'customer_logo' &&
          isBlockPersonalizationEnabled(selectedBlock) && (
          <div
            ref={sidebarRef}
            className="fixed z-100 bottom-8 right-8 top-20 flex w-[320px] max-w-[320px] flex-col rounded-2xl border-l border-border bg-background shadow-lg"
          >
            <BlockDetailsSidebar
              block={selectedBlock}
              onBlockUpdate={editor.handleBlockUpdate}
              organizationId={editor.campaign?.organization?.id}
              onBuyerRemoved={(buyer) => void logoRematch.rematchAfterRemoval(buyer.company?.id)}
            />
          </div>
        )}
        {/* Variants: only the logo banner has a sidebar (its logos, the
            original content, instructions). Text and CTA blocks are edited
            through their badge and the chat, so nothing to show. */}
        {!isOriginalSelected &&
          activeTool !== 'text' &&
          selectedBlockId &&
          selectedVariant &&
          selectedBlock?.type === 'customer_logo' &&
          selectedPage &&
          areVariantsForSelectedPage && (
          <div className="fixed bottom-8 right-8 top-20 z-100 flex w-[320px] max-w-[320px] flex-col rounded-2xl border-l border-border bg-background shadow-lg">
            <PreviewSidebar
              block={selectedBlock}
              variant={selectedVariant}
              page={selectedPage}
              maxLogos={selectedBlock.logoCount ?? 8}
            />
          </div>
        )}
      </StudioLayout>

      {/* Always mounted (unlike the block-details sidebar) so returning via
          the background job-alert link (#buyer-import=<jobId>) reopens the
          import flow. Renders nothing unless that hash is present. */}
      <ResumeBuyerImportModal />

      <CancelGenerationModal
        open={showCancelGeneration}
        onOpenChange={setShowCancelGeneration}
        isPending={cancelGeneration.isPending}
        onConfirm={handleCancelGenerationConfirmed}
      />

      {/* Two flavours of the "changes aren't live" notice: a customers-library
          change re-matches the logos of the generated variants in place (a
          job the studio started, see useLogoRematch) — the CTA only takes the
          user to watch the result; while a block edit — the personalization
          toggle included — never touches the variants; it only flags the
          published pages for republication, so its CTA reuses the existing
          republish flow. */}
      <ChangesNotLiveModal
        open={editor.showChangesNotLiveModal}
        onOpenChange={(next) => !next && editor.dismissChangesNotLive()}
        step={
          isLibraryNotice ? 'your customers library' : 'the personalization scope'
        }
        alertDescription={
          isLibraryNotice
            ? 'The logos shown on your variant pages are being re-matched to your updated library. Published pages will need to be republished.'
            : `Your published variant ${pluralize(
                editor.pagesCount,
                'page'
              )} ${pluralize(editor.pagesCount, 'needs', 'need')} to be republished.`
        }
        callToAction={
          isLibraryNotice
            ? undefined
            : 'Publish the new version of your variant pages.'
        }
        actions={
          isLibraryNotice
            ? [
                { label: 'Continue', onClick: editor.dismissChangesNotLive },
                {
                  label: 'Review',
                  onClick: () => {
                    editor.dismissChangesNotLive();
                    navigate(hostUrls.reviewVariantsUrl);
                  },
                },
              ]
            : [
                { label: 'Continue', onClick: editor.dismissChangesNotLive },
                // No publish flow on this host (back office): Continue only.
                ...(hostUrls.publishUrl
                  ? [
                      {
                        label: 'Review & publish',
                        onClick: () => {
                          editor.dismissChangesNotLive();
                          navigate(hostUrls.publishUrl as string);
                        },
                      },
                    ]
                  : []),
              ]
        }
      />

      {/* Inline text edition outdates a published page just like a sidebar
          edit — same notice, same copy (see PreviewSidebar). */}
      <ChangesNotLiveModal
        {...inlineEditModalProps}
        step="some variant content"
        alertDescription="The edited variant pages need to be published."
        callToAction="Generate and publish the new version of your variant pages."
        actions={[
          { label: 'Ok noted !', variant: 'outline', onClick: () => inlineEditModalProps.onOpenChange(false) },
        ]}
      />
    </>
  );
};
